import { AI_PRIVACY_ENABLED } from '../config';
import type { ChatMessage } from '../core/llm';
import { replaceSensitive } from './detectors';
import {
  PLACEHOLDER_PATTERN,
  PrivacyVault,
  type PlaceholderKind,
  type SerializedVault,
} from './vault';

// ----------------------------------------------------------------------
// Privacy gateway ("standard" level): contact details and personal
// identifiers are swapped for placeholders before anything reaches the model,
// and swapped back wherever a person reads the output or an API receives it.
// Names are left in clear at this level.
// ----------------------------------------------------------------------

export const SECRET_PLACEHOLDER = '[[REDACTED]]';

/**
 * Fields masked by name whatever their format — a phone stored without
 * separators, or an email the detectors would miss. Everything else still goes
 * through the text detectors.
 */
const KEY_POLICY: Record<string, PlaceholderKind | 'SECRET'> = {
  phone: 'PHONE',
  phones: 'PHONE',
  phone_number: 'PHONE',
  old_phone: 'PHONE',
  new_phone: 'PHONE',
  parent_phone: 'PHONE',
  email: 'EMAIL',
  emails: 'EMAIL',
  pinfl: 'ID',
  jshshir: 'ID',
  passport: 'ID',
  passport_number: 'ID',
  password: 'SECRET',
  token: 'SECRET',
  access_token: 'SECRET',
  refresh_token: 'SECRET',
  secret: 'SECRET',
  api_key: 'SECRET',
};

export type UnmaskedArgs = {
  args: unknown;
  unknownPlaceholders: string[];
};

export type PrivacyGateway = {
  /** What the user typed. */
  maskText: (text: string) => string;
  /** A tool result, already projected. */
  maskResult: (value: unknown) => unknown;
  /** A stored conversation — also re-masks history saved before masking existed. */
  maskHistory: (history: ChatMessage[]) => ChatMessage[];
  /** Model output about to be shown to the user. */
  unmaskText: (text: string) => string;
  /** Tool arguments about to be confirmed and sent to the API. */
  unmaskArgs: (args: unknown) => UnmaskedArgs;
  /** `null` when masking is off — nothing to store. */
  serialize: () => SerializedVault | null;
};

// ----------------------------------------------------------------------

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const maskText = (text: string, vault: PrivacyVault): string =>
  replaceSensitive(text, (match) =>
    match.kind === 'SECRET' ? SECRET_PLACEHOLDER : vault.tokenFor(match.kind, match.value, 'text')
  );

const maskValue = (value: unknown, vault: PrivacyVault, key?: string): unknown => {
  if (typeof value === 'string') {
    const policy = key ? KEY_POLICY[key.toLowerCase()] : undefined;
    if (!policy || !value.trim()) return maskText(value, vault);
    return policy === 'SECRET' ? SECRET_PLACEHOLDER : vault.tokenFor(policy, value, 'field');
  }
  if (Array.isArray(value)) {
    // Array items inherit the key, so `phones: ["…", "…"]` is masked item by item.
    return value.map((item) => maskValue(item, vault, key));
  }
  if (isPlainObject(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([entryKey, entry]) => [
        entryKey,
        maskValue(entry, vault, entryKey),
      ])
    );
  }
  return value;
};

/** Tool messages and tool-call arguments are JSON strings: mask them structurally. */
const maskJsonString = (raw: string, vault: PrivacyVault): string => {
  try {
    return JSON.stringify(maskValue(JSON.parse(raw), vault));
  } catch {
    return maskText(raw, vault);
  }
};

const maskContent = <T>(content: T, mask: (text: string) => string): T => {
  if (typeof content === 'string') return mask(content) as T;
  if (Array.isArray(content)) {
    return content.map((part: unknown) =>
      isPlainObject(part) && typeof part.text === 'string'
        ? { ...part, text: mask(part.text) }
        : part
    ) as T;
  }
  return content;
};

const maskMessage = (message: ChatMessage, vault: PrivacyVault): ChatMessage => {
  if (message.role === 'tool') {
    return {
      ...message,
      content: maskContent(message.content, (text) => maskJsonString(text, vault)),
    };
  }
  if (message.role === 'assistant') {
    return {
      ...message,
      content: maskContent(message.content, (text) => maskText(text, vault)),
      ...(message.tool_calls
        ? {
            tool_calls: message.tool_calls.map((call) =>
              call.type === 'function'
                ? {
                    ...call,
                    function: {
                      ...call.function,
                      arguments: maskJsonString(call.function.arguments, vault),
                    },
                  }
                : call
            ),
          }
        : {}),
    };
  }
  return {
    ...message,
    content: maskContent(message.content, (text) => maskText(text, vault)),
  } as ChatMessage;
};

const unmaskText = (text: string, vault: PrivacyVault): string =>
  text.replace(PLACEHOLDER_PATTERN, (token) => vault.valueOf(token) ?? token);

const unmaskArgs = (args: unknown, vault: PrivacyVault): UnmaskedArgs => {
  const unknown = new Set<string>();

  const walk = (value: unknown): unknown => {
    if (typeof value === 'string') {
      return value.replace(PLACEHOLDER_PATTERN, (token) => {
        const real = vault.valueOf(token);
        if (real === undefined) unknown.add(token);
        return real ?? token;
      });
    }
    if (Array.isArray(value)) return value.map(walk);
    if (isPlainObject(value)) {
      return Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, walk(entry)]));
    }
    return value;
  };

  const restored = walk(args);
  return { args: restored, unknownPlaceholders: Array.from(unknown) };
};

// ----------------------------------------------------------------------

const PASSTHROUGH: PrivacyGateway = {
  maskText: (text) => text,
  maskResult: (value) => value,
  maskHistory: (history) => history,
  unmaskText: (text) => text,
  unmaskArgs: (args) => ({ args, unknownPlaceholders: [] }),
  serialize: () => null,
};

/** One gateway per conversation. `saved` is the vault stored with a transcript. */
export const createPrivacyGateway = (saved?: unknown): PrivacyGateway => {
  if (!AI_PRIVACY_ENABLED) return PASSTHROUGH;

  const vault = PrivacyVault.fromJSON(saved);

  return {
    maskText: (text) => maskText(text, vault),
    maskResult: (value) => maskValue(value, vault),
    maskHistory: (history) => history.map((message) => maskMessage(message, vault)),
    unmaskText: (text) => unmaskText(text, vault),
    unmaskArgs: (args) => unmaskArgs(args, vault),
    serialize: () => vault.toJSON(),
  };
};
